/* eslint-disable @typescript-eslint/no-restricted-imports */
import { type FormatOptionsWithTZ, format, formatInTimeZone } from 'date-fns-tz'
import parse from 'date-fns/parse'

/**
 * Date and time format strings for use with the Power date time wrapper functions.
 *
 * @playground
 * Change the versions to be the ones matching in the repo's package.json
 * See: https://stackblitz.com/edit/date-fns-playground-qxbjivk6?file=package.json,index.ts
 *
 * @remarks
 * These format strings are intended to be used with the `format` and `formatInTimeZone` functions
 * from the `date-fns` and `date-fns-tz` libraries. They follow the tokens and conventions defined
 * by `date-fns` (see: https://date-fns.org/v2.30.0/docs/format).
 *
 * @example
 * ```typescript
 * import { powerDateTimeFormat } from './dateTimeFormats'
 *
 * const formatted = powerDateTimeFormat(new Date(), 'yyyy-MM-dd')
 * ```
 *
 * @see {@link https://date-fns.org/v2.30.0/docs/format | date-fns format documentation}
 */
const dateTimeFormat = {
  'EE, MMMM d, yyyy h:mmaaa': 'EE, MMMM d, yyyy h:mmaaa', // Tue, March 5 2024 3:45PM
  'EE, MMMM d: haaaaa': 'EE, MMMM d: haaaaa', // Tue, March 5: 3:45p
  'EEEE, MMMM d, yyyy h:mm a': 'EEEE, MMMM d, yyyy h:mm a', // Tuesday, March 5 2024 3:45PM
  "M/d/yy 'at' h:mmaaa z": "M/d/yy 'at' h:mmaaa z", // 3/5/24 at 3:45PM PST
  'MM/dd/yy, hh:mm a': 'MM/dd/yy, hh:mm a', // 03/05/24, 03:45 PM
  "yyyy-MM-dd'T'HH:mm:ss.SSS": "yyyy-MM-dd'T'HH:mm:ss.SSS", // 2025-03-05T18:30:06.460
} as const

const dateFormat = {
  'EE, MMMM d': 'EE, MMMM d', // Tue, March 5
  'EE, MMMM d, yyyy': 'EE, MMMM d, yyyy', // Tue, March 5 2024
  'EEE, MMM d, yyyy': 'EEE, MMM d, yyyy', // Tue, Mar 5, 2024
  'EEEE, MMM d, yyyy': 'EEEE, MMM d, yyyy', // Tuesday, Mar 5, 2024
  'EEEE, MMMM d': 'EEEE, MMMM d', // Tuesday, March 5
  'EEEE, MMMM d, yyyy': 'EEEE, MMMM d, yyyy', // Tuesday, March 5, 2024
  'LL_yyyy': 'LL_yyyy', // 03_2025
  'M/d': 'M/d', // 3/24
  'M/d/yy': 'M/d/yy', // 3/5/24
  'MM/DD/YYYY': 'MM/DD/YYYY', // 03/05/2024
  'MMM': 'MMM', // Mar
  'MMM d': 'MMM d', // Mar 5
  'MMM d, y': 'MMM d, y', // Mar 5, 2024
  'MMM d, yyyy': 'MMM d, yyyy', // Mar 5, 2024
  'MMM dd, yyyy': 'MMM dd, yyyy', // Mar 05, 2024
  'MMMM d': 'MMMM d', // March 5
  'MMMM d, yyyy': 'MMMM d, yyyy', // March 5, 2024
  'MMMM do': 'MMMM do', // March 5th
  'MMMM yyyy': 'MMMM yyyy', // March 2024
  'MMddyyyy': 'MMddyyyy', // 03052024
  'PP': 'PP', // Mar 5, 2025
  'd': 'd', // 5
  'dd-MMM-yyyy': 'dd-MMM-yyyy', // 05-MAR-2024
  'eeee, MMMM d': 'eeee, MMMM d', // Tuesday, March 5
  'yyyy': 'yyyy', // 2024
  'yyyy-MM-dd': 'yyyy-MM-dd', // 2024-03-05
  'yyyyMMdd': 'yyyyMMdd', // 20240305
} as const

const timeFormat = {
  'HH:mm:ss': 'HH:mm:ss', // 15:45:00
  'h a': 'h a ', // 3 PM
  'h:mm a': 'h:mm a', // 3:45 PM
  'h:mma': 'h:mma', // 3:45PM
  'h:mmaaa': 'h:mmaaa', // 3:45pm
  'haaaaa': 'haaaaa', // 3p
  'p': 'p', // 1:36 PM
} as const

const timeZoneFormat = {
  zzz: 'zzz', // GMT-3
} as const

// Important: Wrapping the text in quotes is used to denote display text
const customFormat = {
  "'Today at' h:mm a": "'Today at' h:mm a", // Today at 1:47 PM
  "'Yesterday at' h:mm a": "'Yesterday at' h:mm a", // Yesterday at 1:47 PM
  "EEEE 'at' h:mm a": "EEEE 'at' h:mm a", // Tuesday at 1:48 PM
} as const

export const timeZones = {
  UTC: 'UTC',
} as const

type DateTimeFormat = keyof typeof dateTimeFormat
type DateFormat = keyof typeof dateFormat
type TimeFormat = keyof typeof timeFormat
type TimeZoneFormat = keyof typeof timeZoneFormat
type CustomFormat = keyof typeof customFormat
type PowerTimeZones = keyof typeof timeZones
type PowerFormatOptionsWithTZ = Omit<FormatOptionsWithTZ, 'timeZone'> & {
  timeZone?: PowerTimeZones
}
type PowerDateTime =
  | DateTimeFormat
  | DateFormat
  | TimeFormat
  | TimeZoneFormat
  | CustomFormat

export function powerDateTimeFormat(
  date: Date | number | string,
  formatStr: PowerDateTime,
  options?: PowerFormatOptionsWithTZ,
): string {
  return format(date, formatStr, options)
}

// Important: The parse function from date-fns/parse doesn't seem to want to
// resolve to a function declaration within the context of a unit test.
// This may be due to it coming from the sendbird ui kit where it is not
// able to be run within a unit test.
export function powerDateTimeParse(
  dateString: string,
  formatString: PowerDateTime,
  referenceDate: Date | number,
  options?: {
    firstWeekContainsDate?: 1 | 2 | 3 | 4 | 5 | 6 | 7
    locale?: Locale
    useAdditionalDayOfYearTokens?: boolean
    useAdditionalWeekYearTokens?: boolean
    weekStartsOn?: 0 | 1 | 2 | 3 | 4 | 5 | 6
  },
): Date {
  return parse(dateString, formatString, referenceDate, options)
}

export function powerFormatInTimeZone(
  date: Date | string | number,
  timeZone: string,
  formatStr: PowerDateTime,
  options?: PowerFormatOptionsWithTZ,
): string {
  return formatInTimeZone(date, timeZone, formatStr, options)
}
